/* ==========================================================================
   NZS Day Stack — AG1-style kit builder (engine, v3).
   Two-level selection per slot:
     • PRODUCT dropdown  → swaps to an entirely different product family.
     • FLAVOUR dropdown  → picks a flavour of the selected family (from the
                            product's custom.bys_flavours), shown only when the
                            family has >1 flavour.
   Changing either swaps image/price/per-serve, re-renders the itemised KIT PANEL
   line and recomputes the bundle total + tiered "You save" (2→6 slots, 1 = 0%).
   Gift blocks unlock free lines at a slot count; subscribe mode gives every kit
   line its own "Every N weeks" stepper. "Add bundle" posts every available line
   (plus unlocked gifts) to /cart/add.js then applies the count-matched code.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- motion system (page-animate two-speed): --slow 600 / --quick 150, one easing ---- */
  var SLOW = 600;
  function prefersReduced() {
    return window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function canAnimate() { return !prefersReduced() && !document.hidden; }
  function canHover() { return !!(window.matchMedia && matchMedia('(hover:hover)').matches); }

  function countUp(el, from, to, format) {
    if (!el) return;
    if (!canAnimate() || typeof from !== 'number' || !isFinite(from) || from === to) {
      el.textContent = format(to); return;
    }
    if (el.__raf) cancelAnimationFrame(el.__raf);
    if (el.__to) clearTimeout(el.__to);
    var ease = function (x) { return 1 - Math.pow(1 - x, 4); };
    var start = null;
    var step = function (ts) {
      if (start == null) start = ts;
      var t = Math.min(1, (ts - start) / SLOW);
      el.textContent = format(from + (to - from) * ease(t));
      if (t < 1) el.__raf = requestAnimationFrame(step);
      else { el.__raf = null; clearTimeout(el.__to); el.textContent = format(to); }
    };
    el.__raf = requestAnimationFrame(step);
    el.__to = setTimeout(function () {                    // rAF pauses in hidden tabs; timers don't
      if (el.__raf) { cancelAnimationFrame(el.__raf); el.__raf = null; }
      el.textContent = format(to);
    }, SLOW + 80);
  }

  function swapImage(img, url, alt) {
    if (!img || !url) return;
    if (alt != null) img.alt = alt;
    if (img.getAttribute('src') === url) return;
    img.classList.add('is-swapping');
    var unstick = function () {
      img.classList.remove('is-swapping');
      if (img.getAnimations) img.getAnimations().forEach(function (a) { a.cancel(); });
    };
    var done = function () { img.src = url; setTimeout(unstick, 30); };
    if (!canAnimate()) { img.src = url; unstick(); return; }
    var pre = new Image();
    pre.onload = done; pre.onerror = done;
    pre.src = url;
    setTimeout(unstick, 1200);                            // hard fallback (timers survive hidden tabs)
  }

  function reveal(el) {
    if (!el) return;
    var wasHidden = el.hidden;
    el.hidden = false;
    if (!wasHidden || prefersReduced()) return;           // animate only on the hidden->shown edge
    el.classList.remove('nzs-rvl'); void el.offsetWidth;
    el.classList.add('nzs-rvl');
    el.addEventListener('animationend', function () { el.classList.remove('nzs-rvl'); }, { once: true });
  }

  /* ---- shared hover-preview card (EA ea-rparts-preview mechanism) ----
     ONE position:fixed node on document.body — never inside the section, whose
     animated/transformed ancestors would break `fixed`. Each section registers a
     resolver on itself; the delegated document listeners look it up from the trigger. */
  var PV = null, pvTimer = null, pvBound = false;
  function ensurePreview() {
    if (PV) return PV;
    var el = document.createElement('div');
    el.id = 'nzs-dstk-preview';
    el.className = 'nzs-dstk-preview';
    el.innerHTML =
      '<div class="nzs-dstk-preview__media"><img class="nzs-dstk-preview__img" data-pv-img alt=""></div>' +
      '<div class="nzs-dstk-preview__body">' +
        '<div class="nzs-dstk-preview__flav" data-pv-flav></div>' +
        '<div class="nzs-dstk-preview__name" data-pv-name></div>' +
        '<div class="nzs-dstk-preview__row">' +
          '<s class="nzs-dstk-preview__was" data-pv-was hidden></s>' +
          '<span class="nzs-dstk-preview__price" data-pv-price></span>' +
          '<span class="nzs-dstk-preview__stock" data-pv-stock></span>' +
        '</div>' +
        '<a class="nzs-dstk-preview__view" data-pv-view href="#"></a>' +
      '</div>';
    document.body.appendChild(el);
    if (!canHover()) el.classList.add('is-touch');
    el.addEventListener('mouseenter', function () { clearTimeout(pvTimer); });
    el.addEventListener('mouseleave', pvHideSoon);
    PV = el;
    return el;
  }
  function pvHideSoon() { clearTimeout(pvTimer); pvTimer = setTimeout(pvHide, 160); }
  function pvHide() { if (PV) { PV.classList.remove('is-visible'); PV.__trig = null; } }
  function pvResolve(trig) {
    var root = trig.closest && trig.closest('[data-nzs-daystack]');
    var fn = root && root.__dstkPv;
    if (!fn) return null;
    return fn(parseInt(trig.getAttribute('data-kpv-i'), 10) || 0);
  }
  function pvShow(trig) {
    var d = pvResolve(trig);
    if (!d) return;
    var card = ensurePreview();
    clearTimeout(pvTimer);
    var img = card.querySelector('[data-pv-img]');
    img.src = d.img || '';
    img.style.display = d.img ? '' : 'none';
    card.querySelector('[data-pv-flav]').textContent = d.flav || '';
    card.querySelector('[data-pv-name]').textContent = d.name || '';
    card.querySelector('[data-pv-price]').textContent = d.price || '';
    var was = card.querySelector('[data-pv-was]');
    if (d.was) { was.textContent = d.was; was.hidden = false; } else was.hidden = true;
    var st = card.querySelector('[data-pv-stock]');
    st.textContent = d.stock || '';
    st.className = 'nzs-dstk-preview__stock ' + (d.avail ? 'is-in' : 'is-out');
    var view = card.querySelector('[data-pv-view]');
    view.textContent = d.viewLabel || 'View product';
    view.setAttribute('href', d.url || '#');
    // measure then clamp: centre over the trigger, flip above/below at the viewport edge
    var r = trig.getBoundingClientRect();
    card.style.visibility = 'hidden';
    card.classList.add('is-visible');
    var cw = card.offsetWidth, ch = card.offsetHeight;
    var left = Math.min(Math.max(8, r.left + r.width / 2 - cw / 2), window.innerWidth - cw - 8);
    var top = r.top - ch - 10;
    if (top < 8) top = r.bottom + 10;
    if (top + ch > window.innerHeight - 8) top = Math.max(8, window.innerHeight - ch - 8);
    card.style.left = left + 'px';
    card.style.top = top + 'px';
    card.style.visibility = '';
    card.__trig = trig;
  }
  function bindPreview() {
    if (pvBound) return;
    pvBound = true;
    if (canHover()) {
      document.addEventListener('mouseover', function (e) {
        var t = e.target.closest && e.target.closest('[data-kpv]');
        if (t) pvShow(t);
      });
      document.addEventListener('mouseout', function (e) {
        var t = e.target.closest && e.target.closest('[data-kpv]');
        if (!t) return;
        var to = e.relatedTarget;
        if (to && to.closest && (to.closest('[data-kpv]') === t || to.closest('.nzs-dstk-preview'))) return;
        pvHideSoon();
      });
      document.addEventListener('focusin', function (e) {
        var t = e.target.closest && e.target.closest('[data-kpv]');
        if (t) pvShow(t); else if (!(e.target.closest && e.target.closest('.nzs-dstk-preview'))) pvHideSoon();
      });
    } else {
      // touch: first tap opens the card (its "View product" link is the nav path)
      document.addEventListener('click', function (e) {
        var t = e.target.closest && e.target.closest('[data-kpv]');
        if (t) {
          if (PV && PV.classList.contains('is-visible') && PV.__trig === t) return;  // second tap navigates
          e.preventDefault(); pvShow(t); return;
        }
        if (!(e.target.closest && e.target.closest('.nzs-dstk-preview'))) pvHide();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !PV || !PV.classList.contains('is-visible')) return;
      var t = PV.__trig;
      pvHide();
      if (t && t.focus) t.focus();
    });
    // capture, on document: inner scrollers (the kit list) don't bubble scroll to window
    document.addEventListener('scroll', pvHide, { capture: true, passive: true });
  }

  /* ---- custom dropdown (nzs-sort-style) over a native <select> state-holder ----
     The select stays in the DOM (visually hidden once enhanced) so no-JS still works and
     all existing change-driven logic is untouched: picking an option sets select.value and
     dispatches a real change event. Panel animates open like the collection Sort popover. */
  function dropSync(sel) {
    var d = sel.__drop; if (!d) return;
    var cur = d.trig.querySelector('.nzs-dstk__dcur');
    var so = sel.options[sel.selectedIndex];
    if (cur) cur.textContent = so ? so.text : '';
    var html = '';
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      html += '<button type="button" role="option" class="nzs-dstk__dopt' +
        (i === sel.selectedIndex ? ' is-active' : '') + '"' +
        (o.disabled ? ' disabled' : '') + ' data-di="' + i + '" aria-selected="' +
        (i === sel.selectedIndex ? 'true' : 'false') + '"><span>' +
        o.text.replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }) +
        '</span><span class="nzs-dstk__ddot" aria-hidden="true"></span></button>';
    }
    d.panel.innerHTML = html;
  }
  function dropClose(sel) {
    var d = sel.__drop; if (!d || !d.wrap.classList.contains('is-dopen')) return;
    d.wrap.classList.remove('is-dopen');
    d.trig.setAttribute('aria-expanded', 'false');
  }
  function enhanceSelect(sel) {
    if (sel.__drop) { dropSync(sel); return; }
    var wrap = sel.closest('.nzs-dstk__selwrap'); if (!wrap) return;
    wrap.classList.add('is-enhanced');
    var trig = document.createElement('button');
    trig.type = 'button'; trig.className = 'nzs-dstk__dtrig';
    trig.setAttribute('aria-haspopup', 'listbox');
    trig.setAttribute('aria-expanded', 'false');
    trig.setAttribute('aria-label', sel.getAttribute('aria-label') || 'Choose an option');
    trig.innerHTML = '<span class="nzs-dstk__dcur"></span>' +
      '<svg class="nzs-dstk__dcaret" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var panel = document.createElement('div');
    panel.className = 'nzs-dstk__dpanel';
    panel.setAttribute('role', 'listbox');
    wrap.appendChild(trig); wrap.appendChild(panel);
    sel.__drop = { trig: trig, panel: panel, wrap: wrap };
    trig.addEventListener('click', function () {
      var open = wrap.classList.contains('is-dopen');
      Array.prototype.forEach.call(document.querySelectorAll('.nzs-dstk__selwrap.is-dopen select'), dropClose);
      if (!open) {
        dropSync(sel);
        wrap.classList.add('is-dopen');
        trig.setAttribute('aria-expanded', 'true');
      }
    });
    trig.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault(); trig.click();
        var act = panel.querySelector('.is-active:not([disabled])') || panel.querySelector('.nzs-dstk__dopt:not([disabled])');
        if (act) act.focus();
      }
    });
    panel.addEventListener('keydown', function (e) {
      var opts = Array.prototype.filter.call(panel.querySelectorAll('.nzs-dstk__dopt'), function (b) { return !b.disabled; });
      var idx = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (opts[idx + 1] || opts[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (opts[idx - 1] || opts[opts.length - 1]).focus(); }
      else if (e.key === 'Escape') { dropClose(sel); trig.focus(); }
    });
    panel.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('.nzs-dstk__dopt');
      if (!b || b.disabled) return;
      sel.selectedIndex = parseInt(b.getAttribute('data-di'), 10);
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      dropSync(sel);
      dropClose(sel);
      trig.focus();
    });
    dropSync(sel);
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.nzs-dstk__selwrap.is-enhanced')) return;
    Array.prototype.forEach.call(document.querySelectorAll('.nzs-dstk__selwrap.is-dopen select'), dropClose);
  });

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nzs-daystack]'), init);
  }

  function init(root) {
    if (root.dataset.dstkInit) return;
    root.dataset.dstkInit = '1';

    var dataEl = root.querySelector('[data-nzs-daystack-data]');
    var CFG;
    try { CFG = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var SLOTS = CFG.slots || [];
    var GIFTS = CFG.gifts || [];
    var LB = CFG.labels || {};
    var fmt = root.getAttribute('data-money-format') || ('$' + '{{amount}}');

    function fwd(n, p, t, d) {
      p = (p == null ? 2 : p); t = (t == null ? ',' : t); d = (d == null ? '.' : d);
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(p); var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
      return dollars + (parts[1] ? d + parts[1] : '');
    }
    function money(cents) {
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/), key = m && m[1], v;
      if (key === 'amount_no_decimals') v = fwd(cents, 0);
      else if (key === 'amount_with_comma_separator') v = fwd(cents, 2, '.', ',');
      else if (key === 'amount_no_decimals_with_comma_separator') v = fwd(cents, 0, '.', ',');
      else v = fwd(cents, 2);
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, v);
    }
    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    function firstAvailFlav(product) {
      if (!product || !product.flavours || !product.flavours.length) return -1;
      for (var i = 0; i < product.flavours.length; i++) if (product.flavours[i].av) return i;
      return 0;
    }
    // supply-duration preset: one serve a day → round(servings / 7) weeks, clamped 1..16
    function weekPreset(f) {
      var s = f && Number(f.serv);
      if (!s || !isFinite(s) || s <= 0) return 4;
      return Math.max(1, Math.min(16, Math.round(s / 7)));
    }

    // per-slot state: {pi: product/family index, fi: flavour index, on, week}
    var state = SLOTS.map(function (slot) {
      var pi = 0, fi = firstAvailFlav(slot.products[0]);
      var f0 = slot.products[0] && slot.products[0].flavours[fi];
      return { pi: pi, fi: fi, on: !slot.up, week: weekPreset(f0), wkTouched: false };
    });
    function curProduct(i) { return SLOTS[i].products[state[i].pi]; }
    function curFlav(i) { var p = curProduct(i); return (p && p.flavours[state[i].fi]) || null; }

    var mode = 'onetime';                    // 'onetime' | 'sub'
    var SUBPCT = Number(CFG.subpct) || 5;
    var SUBPLANS = CFG.subplans || null;

    // {id, week} for the plan that would actually be attached — week is the RESOLVED one, which
    // is not the asked-for week when the array falls back to the 4-weekly slot.
    function planSlot(vid, ck, week) {
      var rec = SUBPLANS && SUBPLANS[String(vid)];
      var ent = rec && rec[ck];
      if (!ent) return null;
      if (Object.prototype.toString.call(ent) === '[object Array]') {
        if (ent[week - 1]) return { id: ent[week - 1], week: week };
        if (ent[3]) return { id: ent[3], week: 4 };          // 4-weekly fallback
        return null;
      }
      return { id: ent, week: week };                        // legacy single-id map
    }
    function planFor(vid, ck, week) { var s = planSlot(vid, ck, week); return s && s.id; }
    function planKey(n) { return n >= 4 ? '4' : n >= 3 ? '3' : '2'; }   // count 1 rides the '2' plans

    function setMode(m) {
      mode = m;
      Array.prototype.forEach.call(root.querySelectorAll('[data-buyopt]'), function (o) {
        o.classList.toggle('is-on', o.getAttribute('data-buyopt') === m);
      });
      var fr = root.querySelector('[data-freqrow]');        // legacy global slider (cached templates)
      if (fr) { if (m === 'sub') reveal(fr); else fr.hidden = true; }
      var ms = root.querySelector('[data-micro-sub]');
      if (ms) { if (m === 'sub') reveal(ms); else ms.hidden = true; }
      Array.prototype.forEach.call(root.querySelectorAll('[data-klfreq]'), function (el) {
        el.hidden = m !== 'sub';
      });
      recalc();
    }

    function tierKey(n) { return n >= 6 ? '6' : n >= 5 ? '5' : n >= 4 ? '4' : n >= 3 ? '3' : n >= 2 ? '2' : null; }
    function discountPct(n) { var k = tierKey(n); return k ? (Number(CFG.pct[k]) || 0) : 0; }
    function discountCode(n) { var k = tierKey(n); return k ? (CFG.codes[k] || '') : ''; }

    function renderSlot(i) {
      var slotEl = root.querySelector('[data-slot="' + i + '"]');
      if (!slotEl) return;
      var p = curProduct(i), f = curFlav(i);
      slotEl.classList.toggle('is-out', !(f && f.av));
      var on = state[i].on;
      slotEl.classList.toggle('is-off', !on);
      var tg = slotEl.querySelector('[data-toggle]');
      if (tg) { tg.setAttribute('aria-pressed', on ? 'true' : 'false'); tg.title = on ? 'In your stack — click to skip' : 'Skipped — click to add'; }

      var img = slotEl.querySelector('[data-img]');
      var src = (f && f.img) || (p && p.img) || '';
      if (img && src) swapImage(img, src, (p && p.base) || '');

      setSel(slotEl, '[data-brand]', p && p.br);
      setSel(slotEl, '[data-title]', p && p.base);

      var priceEl = slotEl.querySelector('[data-price]');
      if (priceEl && f) {
        var now = priceEl.querySelector('[data-now]'), was = priceEl.querySelector('[data-was]');
        if (now) { now.textContent = money(f.price); now.classList.toggle('is-sale', f.c > f.price); }
        if (was) { if (f.c > f.price) { was.textContent = money(f.c); was.hidden = false; } else was.hidden = true; }
      }
      var chip = slotEl.querySelector('[data-savechip]');
      if (chip) {
        if (f && f.c > f.price) { chip.textContent = 'SAVE ' + money(f.c - f.price); chip.hidden = false; }
        else chip.hidden = true;
      }

      var serve = slotEl.querySelector('[data-serve]');
      if (serve) {
        var line = '';
        if (f && f.serv) { line = money(Math.round(f.price / f.serv)) + (LB.serve || '/serve') + ' · ' + f.serv + ' serves'; if (p && p.net) line += ' · ' + p.net + 'g'; }
        serve.textContent = line;
      }

      /* NZS-DSTK-E2 — benefits belong to the PRODUCT family, so they have to repaint
         when the Product dropdown swaps families (the magnesium slot has four). */
      var bens = slotEl.querySelector('[data-bens]');
      if (bens) {
        var bl = (p && p.bens) || [];
        var bh = '', bi;
        for (bi = 0; bi < bl.length && bi < 2; bi++) {
          bh += '<li class="nzs-dstk__ben"><svg viewBox="0 0 16 16" aria-hidden="true">' +
            '<path d="m3 8.5 3 3 7-7.5" fill="none" stroke="currentColor" stroke-width="2" ' +
            'stroke-linecap="round" stroke-linejoin="round"/></svg><span>' +
            escapeHtml(bl[bi]) + '</span></li>';
        }
        bens.innerHTML = bh;
        bens.hidden = !bh;
      }

      // rebuild the flavour <select> for the current product (toggle the labelled field)
      var fsel = slotEl.querySelector('[data-fsel]');
      var ffield = slotEl.querySelector('[data-ffield]');
      if (fsel && ffield) {
        if (p && p.flavours.length > 1) {
          ffield.hidden = false;
          fsel.innerHTML = p.flavours.map(function (fl, idx) {
            return '<option value="' + idx + '"' + (idx === state[i].fi ? ' selected' : '') +
              (fl.av ? '' : ' disabled') + '>' + escapeHtml(fl.label) + (fl.av ? '' : ' · ' + (LB.soldout || 'Sold out')) + '</option>';
          }).join('');
          if (fsel.__drop) dropSync(fsel);
        } else {
          ffield.hidden = true;
        }
      }
      renderKit(i);
    }
    function setSel(el, sel, txt) { var n = el.querySelector(sel); if (n) n.textContent = (txt == null ? '' : txt); }

    // itemised kit-panel line for slot i (mirrors the server-rendered markup)
    function renderKit(i) {
      var li = root.querySelector('[data-kli="' + i + '"]');
      if (!li) return;
      var p = curProduct(i), f = curFlav(i);
      li.hidden = !(state[i].on && f && f.av);
      var url = (f && f.url) || (p && p.url) || '';
      Array.prototype.forEach.call(li.querySelectorAll('[data-klink]'), function (a) {
        if (url) a.setAttribute('href', url);
      });
      var im = li.querySelector('[data-klimg]');
      if (im) { var src = (f && f.img) || (p && p.img) || ''; if (src) im.src = src; }
      var t = li.querySelector('[data-klt]');
      if (t) t.textContent = (p && p.base) || '';
      var sub = li.querySelector('[data-klsub]');
      if (sub) {
        var s = (f && f.label) || '';
        if (f && f.serv) s += (s ? ' · ' : '') + money(Math.round(f.price / f.serv)) + (LB.serve || '/serve');
        sub.textContent = s;
      }
      var now = li.querySelector('[data-klnow]'), was = li.querySelector('[data-klwas]');
      if (now && f) now.textContent = money(f.price);
      if (was) { if (f && f.c > f.price) { was.textContent = money(f.c); was.hidden = false; } else was.hidden = true; }
      var fq = li.querySelector('[data-klfreq]');
      if (fq) {
        fq.hidden = mode !== 'sub';
        var v = fq.querySelector('[data-stepv]'), u = fq.querySelector('[data-stepu]');
        if (v) v.textContent = state[i].week;
        if (u) u.textContent = state[i].week === 1 ? (LB.unit1 || 'week') : (LB.unit || 'weeks');
      }
      markFirstKit();
    }
    // the divider-less line is the first VISIBLE one, which :first-child gets wrong the moment
    // slot 0 is skipped or is an add-on
    function markFirstKit() {
      var first = true;
      Array.prototype.forEach.call(root.querySelectorAll('[data-kli]'), function (el) {
        var vis = !el.hidden;
        el.classList.toggle('is-first', vis && first);
        if (vis) first = false;
      });
    }

    function renderGifts(count) {
      for (var gi = 0; gi < GIFTS.length; gi++) {
        var g = GIFTS[gi];
        var open = count >= (Number(g.unlock) || 0);
        var li = root.querySelector('[data-gli="' + gi + '"]');
        if (li) {
          li.classList.toggle('is-locked', !open);
          var val = li.querySelector('[data-glval]'), lock = li.querySelector('[data-gllock]');
          if (val) val.hidden = !open;
          if (lock) lock.hidden = open;
        }
        var card = root.querySelector('[data-gcard="' + gi + '"]');
        if (card) {
          card.classList.toggle('is-locked', !open);
          var st = card.querySelector('[data-gcstate]');
          if (st) st.textContent = open
            ? (LB.unlocked || 'Unlocked — in your kit')
            : String(LB.lock || 'Unlocks at [n] items').replace('[n]', g.unlock);
        }
      }
    }

    function recalc() {
      var count = 0, subtotal = 0, subEligible = 0, subResolved = 0, live = [], i;
      for (i = 0; i < SLOTS.length; i++) {
        var f = curFlav(i);
        if (state[i].on && f && f.av) {
          count++; subtotal += f.price;
          live.push({ i: i, f: f });
        }
      }
      // Subscribe pricing counts a line ONLY when a plan actually resolves for its own week
      // (a vid present in the map with an empty row buys nothing). Where the array falls back to
      // the 4-weekly slot, snap the stepper to that week so the UI can't promise an interval
      // checkout won't deliver.
      var ck = planKey(count);
      for (i = 0; i < live.length; i++) {
        var sp = planSlot(live[i].f.vid, ck, state[live[i].i].week);
        if (!sp) continue;
        subResolved++;                                       // NZS-DSTK-F3
        subEligible += live[i].f.price;
        if (sp.week !== state[live[i].i].week) {
          state[live[i].i].week = sp.week;
          renderKit(live[i].i);
        }
      }
      var tierPct = discountPct(count);
      var tierSave = Math.round(subtotal * tierPct / 100);           // one-time bundle discount (code)
      // subscribe = tier + extra % baked into a recurring plan (defaults 2->10, 3->15, 4+->20), subscribable lines only
      var subRate = (Number(CFG.pct[ck]) || 0) + SUBPCT;
      var subSave = Math.round(subEligible * subRate / 100);
      /* NZS-DSTK-F3 2026-07-30 — when NOT ONE live line resolves a selling plan, the add
         handler sets subAttached === 0 and falls back to the one-time discount code, so
         checkout charges the ONE-TIME total. Showing subtotal (i.e. MORE than one-time)
         against "Subscribe & save" was therefore both wrong and backwards. Mirror the
         fallback instead of inventing a discount the store cannot honour.
         This is what the staged review page hits: /pages/fuel-your-whole-day carries the
         original Day Stack subplan map, which covers 0 of Blake's 5 default picks. */
      if (subResolved === 0) subSave = tierSave;
      // buy-toggle per-mode prices
      setText('[data-buy-onetime]', money(subtotal - tierSave));
      setText('[data-buy-sub]', money(subtotal - subSave));
      // selected mode drives the summary rows
      var isSub = mode === 'sub';
      var saving = isSub ? subSave : tierSave;
      var pctLabel = isSub ? subRate : tierPct;
      /* NZS-RRP15-0806 (honest ledger): subtotal is the REAL sell prices and the only savings
         row is the code-backed bundle discount. Quoting subtotal at RRP manufactured a "sale
         saving" out of the store-wide compare-at markup — per-product markdowns already show
         on every card and PDP. */
      setText('[data-sub]', money(subtotal));
      var subWas = root.querySelector('[data-subwas]');
      if (subWas) { if (saving > 0) { subWas.textContent = money(subtotal); subWas.hidden = false; } else subWas.hidden = true; }
      var totEl = root.querySelector('[data-tot]');
      var newTot = subtotal - saving;
      if (totEl) {
        countUp(totEl, (typeof root.__totC === 'number' ? root.__totC : newTot), newTot, money);
        root.__totC = newTot;
      }
      var saleRow = root.querySelector('[data-salerow]');
      if (saleRow) saleRow.hidden = true;   /* NZS-RRP15-0806: RRP-derived row retired */
      var saveRow = root.querySelector('[data-save]');
      if (saveRow) {
        if (saving > 0) { reveal(saveRow); setText('[data-save-amt]', money(saving)); setText('[data-save-pct]', pctLabel + '%'); }
        else saveRow.hidden = true;
      }
      var totRow = root.querySelector('[data-save-total]');
      if (totRow) totRow.hidden = true;   /* NZS-RRP15-0806: RRP-derived row retired */
      renderGifts(count);
      /* NZS-DSTK-E5 — combined RRP of the gifts unlocked at this count. Its own value row:
         the gifts ride along as $0 cart lines, outside "subtotal − bundle savings = Total". */
      var giftVal = 0, gvi;
      for (gvi = 0; gvi < GIFTS.length; gvi++) {
        if (count >= (Number(GIFTS[gvi].unlock) || 0)) giftVal += Number(GIFTS[gvi].rrp) || 0;
      }
      var giftRow = root.querySelector('[data-giftrow]');
      if (giftRow) {
        if (giftVal > 0) { reveal(giftRow); setText('[data-gift-amt]', money(giftVal)); }
        else giftRow.hidden = true;
      }
      var addBtn = root.querySelector('[data-add]');
      if (addBtn) addBtn.disabled = count < 1;
      root._count = count;
    }
    function setText(sel, txt) { var el = root.querySelector(sel); if (el) el.textContent = txt; }

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.hasAttribute && t.hasAttribute('data-buymode')) {        // keyboard-driven radio (arrows/space)
        setMode(t.value);
        return;
      }
      var slotEl = t.closest && t.closest('[data-slot]');
      if (!slotEl) return;
      var i = parseInt(slotEl.getAttribute('data-slot'), 10);
      if (t.hasAttribute('data-psel')) {
        state[i].pi = parseInt(t.value, 10) || 0;
        state[i].fi = firstAvailFlav(curProduct(i));
        if (!state[i].wkTouched) state[i].week = weekPreset(curFlav(i));
        renderSlot(i);
      } else if (t.hasAttribute('data-fsel')) {
        state[i].fi = parseInt(t.value, 10) || 0;
        if (!state[i].wkTouched) state[i].week = weekPreset(curFlav(i));
        renderSlot(i);
      }
      recalc();
    });

    // deselect / re-select a slot (the corner checkmark), buy-mode toggle, per-line steppers
    root.addEventListener('click', function (e) {
      var stepb = e.target.closest && e.target.closest('[data-step]');
      if (stepb) {
        var kli = stepb.closest('[data-kli]');
        if (!kli) return;
        var si = parseInt(kli.getAttribute('data-kli'), 10);
        var dir = parseInt(stepb.getAttribute('data-step'), 10) || 0;
        state[si].week = Math.max(1, Math.min(16, state[si].week + dir));
        state[si].wkTouched = true;
        recalc();       // may snap the week back to a plan the store can honour + refreshes sub pricing
        renderKit(si);  // repaint the stepped line even when recalc didn't snap it
        return;
      }
      var opt = e.target.closest && e.target.closest('[data-buyopt]');
      if (opt) {
        var inp = opt.querySelector('[data-buymode]');
        if (inp) inp.checked = true;
        setMode(opt.getAttribute('data-buyopt'));
        return;
      }
      var tg = e.target.closest && e.target.closest('[data-toggle]');
      if (!tg) return;
      var slotEl = tg.closest('[data-slot]');
      if (!slotEl) return;
      var i = parseInt(slotEl.getAttribute('data-slot'), 10);
      state[i].on = !state[i].on;
      renderSlot(i);
      recalc();
    });

    var addBtn = root.querySelector('[data-add]'), errEl = root.querySelector('[data-err]');
    function showErr(msg) { if (errEl) { errEl.hidden = false; errEl.textContent = msg; } }
    function handleOf(url) {
      var match = String(url || '').split('?')[0].match(/\/products\/([^\/?#]+)/);
      return match ? match[1] : '';
    }

    if (addBtn) addBtn.addEventListener('click', function () {
      if (addBtn.disabled || addBtn.classList.contains('is-loading')) return;
      var items = [], handles = [], subAttached = 0, i;
      var count = 0;
      for (i = 0; i < SLOTS.length; i++) {
        var fc = curFlav(i);
        if (state[i].on && fc && fc.av) count++;
      }
      var ck = planKey(count);
      for (i = 0; i < SLOTS.length; i++) {
        if (!state[i].on) continue;
        var f = curFlav(i);
        if (!f || !f.av || !f.vid) continue;
        var item = { id: parseInt(f.vid, 10), quantity: 1, properties: { _Bundle: CFG.handle || 'Day Stack' } };
        if (mode === 'sub' && SUBPLANS && SUBPLANS[String(f.vid)]) {
          var spid = planFor(f.vid, ck, state[i].week);
          if (spid) { item.selling_plan = parseInt(spid, 10); subAttached++; }
        }
        items.push(item);
        handles.push(handleOf(f.url));
      }
      if (!items.length) return;
      // unlocked gifts ride along as $0 lines — never a selling plan, so they're mode-independent
      for (var gi = 0; gi < GIFTS.length; gi++) {
        var g = GIFTS[gi];
        if (!g.vid || count < (Number(g.unlock) || 0)) continue;
        items.push({ id: parseInt(g.vid, 10), quantity: 1, properties: { _Bundle: CFG.handle || 'Day Stack', _nzs_gift: 'bundle-kit' } });
        handles.push(handleOf(g.url));
      }
      if (errEl) errEl.hidden = true;
      addBtn.classList.add('is-loading');
      var add = window.nzsATC
        ? window.nzsATC.add({ items: items }, { handles: handles })
        : Promise.reject(new Error('Please try again'));
      add.then(function () {
        // one-time applies the bundle tier code; subscribe rides its selling plans (codes don't stack with subs).
        // Safety net: if sub mode attached NO plans (unsubscribable picks), fall back to the one-time code.
        var code = (mode === 'sub' && subAttached > 0) ? '' : discountCode(count);
        window.location.href = code ? ('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart')) : '/cart';
      }).catch(function (err) { addBtn.classList.remove('is-loading'); showErr((err && err.message) || 'Network error. Please try again.'); });
    });

    // legacy global frequency slider — only present on cached/older markup; drives every untouched line
    var freqInp = root.querySelector('[data-freq]');
    if (freqInp) freqInp.addEventListener('input', function () {
      var wk = Math.max(1, Math.min(16, parseInt(freqInp.value, 10) || 4));
      var v = root.querySelector('[data-freqval]'), u = root.querySelector('[data-frequnit]');
      if (v) v.textContent = wk;
      if (u) u.textContent = wk === 1 ? (LB.unit1 || 'week') : (LB.unit || 'weeks');
      freqInp.setAttribute('aria-valuetext', 'Every ' + wk + (wk === 1 ? ' week' : ' weeks'));
      for (var i = 0; i < state.length; i++) {
        if (state[i].wkTouched) continue;
        state[i].week = wk;
        renderKit(i);
      }
      recalc(); // re-resolve plans for the new weeks (may snap lines back + refresh sub pricing)
    });

    // hover-preview data resolver — the shared body-level card reads the live state
    root.__dstkPv = function (i) {
      if (!SLOTS[i]) return null;
      var p = curProduct(i), f = curFlav(i);
      if (!p) return null;
      return {
        img: (f && f.img) || p.img || '',
        name: p.base || '',
        flav: (f && f.label) || '',
        price: f ? money(f.price) : '',
        was: (f && f.c > f.price) ? money(f.c) : '',
        avail: !!(f && f.av),
        stock: (f && f.av) ? (LB.instock || 'In stock') : (LB.soldout || 'Sold out'),
        url: (f && f.url) || p.url || '',
        viewLabel: (LB.view || 'View product') + ' →'
      };
    };
    bindPreview();

    for (var i = 0; i < SLOTS.length; i++) renderSlot(i);
    recalc();
    Array.prototype.forEach.call(root.querySelectorAll('[data-psel],[data-fsel]'), enhanceSelect);
    // self-clear the entrance stagger: its filled transform/opacity animation creates a stacking
    // context that would trap the dropdown panels under the summary box
    Array.prototype.forEach.call(root.querySelectorAll('.nzs-dstk__stag'), function (el) {
      var clear = function () { el.classList.remove('nzs-dstk__stag'); };
      el.addEventListener('animationend', clear, { once: true });
      setTimeout(clear, 1500);                            // guarantee (hidden tab / already finished)
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
